import { useNavigate } from 'react-router-dom';
import { Button, Icon } from '@repo/ui';
import { PageLayout } from '@xtnd/web/shared/layout';
import {
  ClioToDynamoReplicationCard,
  DWEnablementConfigurationCard,
  ErrorState,
  LoadingState,
} from '../components';
import {
  useTenantConfig,
  useReplicationSchedulerConfigMutation,
} from '../queries';
import type { ClioReplicationSchedulerConfig } from '@xtnd/web/shared/types/tenantSettingTypes';

export default function DataReplicationConfigurationPage() {
  const navigate = useNavigate();
  const {
    data: tenantConfig,
    isLoading,
    isError,
    error,
    refetch,
  } = useTenantConfig();

  const replicationConfigMutation = useReplicationSchedulerConfigMutation();

  if (isError && error instanceof Error) {
    return <ErrorState error={error} onRetry={() => refetch()} />;
  }

  const schedulerConfig =
    (tenantConfig?.modules?.clioReplicationScheduler ||
      tenantConfig?.modules?.['clio-replication-scheduler']) as
      | ClioReplicationSchedulerConfig
      | undefined;

  const handleSave = async (config: ClioReplicationSchedulerConfig) => {
    await replicationConfigMutation.mutateAsync(config);
  };

  return (
    <PageLayout
      title="Data Replication Configuration"
      description="Configure Clio to DynamoDB replication and DW or Redshift extraction settings"
      icon="HiAdjustments"
      isLoading={isLoading}
      customSkeleton={<LoadingState />}
      breadcrumbs={[
        { label: 'Settings', href: '/settings' },
        { label: 'Data Replication' },
        { label: 'Configuration' },
      ]}
    >
      <div className="space-y-6">
        <Button
          variant="outline"
          size="sm"
          leftIcon={<Icon name="HiArrowLeft" className="h-4 w-4" />}
          onClick={() => navigate('/settings')}
        >
          Back to Settings
        </Button>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <ClioToDynamoReplicationCard
            config={schedulerConfig}
            isSaving={replicationConfigMutation.isPending}
            onSave={handleSave}
          />

          <DWEnablementConfigurationCard
            config={schedulerConfig}
            isSaving={replicationConfigMutation.isPending}
            onSave={handleSave}
          />
        </div>
      </div>
    </PageLayout>
  );
}
